import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  ReportGmailerrorred,
  TaskAlt,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo, useState } from 'react';

import { breedLib } from 'entities/breed';

import { Breed, Pagination } from 'shared/api';
import { ProgressHelp } from 'shared/assets/svg';

type Column = {
  id: string;
  label: string | React.ReactNode;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'name', label: 'Name (EN)', sortable: true, align: 'left' },
  { id: 'name_ru', label: 'Name (RU)', sortable: true, align: 'left' },
  { id: 'code', label: 'Code', sortable: true, align: 'left' },
  { id: 'specie', label: 'Specie', sortable: true, align: 'left' },
  { id: 'verified', label: <ProgressHelp />, sortable: true, align: 'center' },
  { id: 'descriptions', label: 'Description', sortable: false, align: 'left' },
  { id: 'control', label: 'Control', sortable: false, align: 'center' },
];

type Props = {
  breeds: Breed[] | undefined;
  openBreed?: (code: string) => void;
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (sortBy: string) => void;
  onEdit?: (breed: Breed) => void;
  onDelete?: (breed: Breed) => void;
};

export const BreedsTable = memo(
  ({
    breeds,
    openBreed,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
    onDelete,
  }: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedBreed, setSelectedBreed] = useState<Breed | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, breed: Breed) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedBreed(breed);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedBreed(null);
    };

    const handleEdit = () => {
      if (selectedBreed && onEdit) {
        onEdit(selectedBreed);
      }
      handleMenuClose();
    };

    const handleDelete = () => {
      if (selectedBreed && onDelete) {
        onDelete(selectedBreed);
      }
      handleMenuClose();
    };
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1); // MUI использует 0-based, API использует 1-based
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Индикатор загрузки */}
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        {/* Затемнение таблицы при загрузке */}
        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => handleSortRequest(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {breeds && breeds.length > 0 ? (
                  breeds.map((breed) => (
                    <TableRow
                      key={breed._id}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&.MuiTableRow-hover:hover': {
                          cursor: openBreed ? 'pointer' : 'default',
                        },
                      }}
                      hover={!!openBreed}
                      onClick={() => openBreed?.(breed._id)}
                    >
                      <TableCell align="left">{breed.name}</TableCell>
                      <TableCell align="left">{breed.name_ru}</TableCell>
                      <TableCell align="left">{breed.code}</TableCell>
                      <TableCell align="left">{breedLib.getSpecieText(breed.specie)}</TableCell>
                      <TableCell align="center">
                        {breed.verified ? <TaskAlt color="success" /> : <ReportGmailerrorred color="warning" />}
                      </TableCell>
                      <TableCell align="left">{breed.descriptions ?? '-'}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, breed)} aria-label="more options">
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Породы не найдены
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Меню действий */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1} // MUI использует 0-based
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

BreedsTable.displayName = 'BreedsTable';
